import WorkorderChecklistFooter from '@/components/Movements/WorkOrders/WorkorderChecklist/ChecklistFooter';
import { useWorkorderChecklistContext } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider';
import { WorkorderChecklistItemType } from '@/components/Movements/WorkOrders/WorkorderChecklist/WorkorderChecklistProvider/WorkorderChecklistProvider.types';
import { DefaultRouteTabs, Group } from '@/components/_common';
import {
  useWorkOrderChecklistQuickCreate,
  useWorkOrderChecklistQuickList,
} from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.hooks';
import { workorderChecklistQuickListOptions } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.QuickConstant';
import { WorkOrderChecklistQuickAnswer } from '@/core/domain/workorder/WorkOrdersChecklist/WorkOrdersChecklist.types';
import useChecklistValidRoutes from '@/hooks/useChecklistValidRoutes';
import { PageProps } from '@/types/PageProps';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Table,
  Tooltip,
  Space,
  Input,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaQuestionCircle } from 'react-icons/fa';
import { useIntl } from 'react-intl';
import './styles.less';

type FormItems = { response: string; description: string; comment?: string }[];

const WorkorderChecklistQuickChecklistPage: React.FC<PageProps> = ({
  route,
}) => {
  const intl = useIntl();
  const { checklistItemId, checklistItemType, workorderId, boschChecklist } =
    useWorkorderChecklistContext();

  const [form] = Form.useForm<{ items: FormItems }>();

  const items = Form.useWatch('items', form);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  const { data, isLoading, dataUpdatedAt } = useWorkOrderChecklistQuickList({
    diagnosticId:
      checklistItemType === WorkorderChecklistItemType.Diagnostic
        ? checklistItemId
        : undefined,
    workOrderComplementId:
      checklistItemType === WorkorderChecklistItemType.Workorder
        ? checklistItemId
        : undefined,
  });

  const checklistQuickCreate = useWorkOrderChecklistQuickCreate();

  const routeParent = useChecklistValidRoutes({
    route,
    checklistItemType,
    workorderId,
    boschChecklist,
  });

  useEffect(() => {
    if (data?.length) {
      const dataValues = data?.map((item) => ({
        response: item.response ? '1' : '0',
        description: item.description,
        comment: item.comment,
      }));

      form.setFieldValue(
        'items',
        workorderChecklistQuickListOptions.map((item) => ({
          response: dataValues?.find(
            (dataItem) => dataItem.description === item.value,
          )?.response,
          description: item.value,
          comment: dataValues?.find(
            (dataItem) => dataItem.description === item.value,
          )?.comment,
        })),
      );
      setSelectedKeys(dataValues.map((item) => item.description));
    }
  }, [dataUpdatedAt]);

  async function handleSubmit() {
    try {
      const formItems = (form.getFieldValue('items') as FormItems).filter(
        (item) => !!item.response?.length,
      );

      const answer =
        data
          ?.filter((item) => {
            const itemFinded = formItems.find(
              (fieldItem) => fieldItem.description === item.description,
            );

            return itemFinded?.response.length;
          })
          .map<WorkOrderChecklistQuickAnswer>((item) => {
            const itemFinded = formItems.find(
              (fieldItem) => fieldItem.description === item.description,
            );

            return {
              description: item.description,
              response: itemFinded?.response[0] === '1',
              comment: itemFinded?.comment,
            };
          }) || [];

      const answerSelected = formItems
        .filter((formItem) => formItem.response.length > 0)
        .filter(
          (formItem) =>
            !data?.some((item) => item.description === formItem.description),
        )
        .map<WorkOrderChecklistQuickAnswer>((formItem) => ({
          description: formItem.description,
          response: formItem.response[0] === '1',
          comment: formItem.comment,
        }));

      if (checklistItemType === WorkorderChecklistItemType.Diagnostic) {
        await checklistQuickCreate.mutateAsync({
          answer: [...answer, ...answerSelected],
          diagnosticId: checklistItemId,
        });
      } else if (checklistItemType === WorkorderChecklistItemType.Workorder) {
        await checklistQuickCreate.mutateAsync({
          answer: [...answer, ...answerSelected],
          workOrderComplementId: checklistItemId,
        });
      }
    } catch {}
  }

  const isOkAllSelected = useMemo(() => {
    return items?.length > 0 && !!items?.every((item) => item.response === '1');
  }, [items]);

  const isNotOkAllSelected = useMemo(() => {
    return items?.length > 0 && !!items?.every((item) => item.response === '0');
  }, [items]);

  function handleSetAllOk(checked: boolean) {
    form.setFieldValue(
      'items',
      checked
        ? workorderChecklistQuickListOptions.map((item) => ({
            response: checked ? '1' : undefined,
            description: item.value,
          }))
        : [],
    );
  }

  function handleSetAllNotOk(checked: boolean) {
    form.setFieldValue(
      'items',
      checked
        ? workorderChecklistQuickListOptions.map((item) => ({
            response: checked ? '0' : undefined,
            description: item.value,
          }))
        : [],
    );
  }

  return (
    <DefaultRouteTabs
      route={routeParent}
      baseKey={String(checklistItemId)}
      basePath={`/app/movements/workorderChecklist/${checklistItemId}`}
      extra={
        <Button
          block
          type="primary"
          onClick={handleSubmit}
          loading={checklistQuickCreate.isLoading}
        >
          Salvar checklist rápido
        </Button>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <Form form={form}>
        <Form.List name="items">
          {() => (
            <Table
              loading={isLoading}
              dataSource={workorderChecklistQuickListOptions}
              rowKey={(item) => item.value}
              pagination={false}
              className="quick-checklist"
              columns={[
                {
                  dataIndex: 'response',
                  title: (
                    <Space>
                      <Group gap={10}>
                        <Checkbox
                          checked={isOkAllSelected}
                          onChange={(e) => handleSetAllOk(e.target.checked)}
                        />

                        <span>Ok</span>
                      </Group>

                      <Divider type="vertical" />

                      <Group>
                        <Group gap={10}>
                          <Checkbox
                            checked={isNotOkAllSelected}
                            onChange={(e) =>
                              handleSetAllNotOk(e.target.checked)
                            }
                          />

                          <Group gap={4}>
                            <span>Não ok</span>

                            <Tooltip
                              title={
                                <>
                                  <strong>Checklist rápido</strong>
                                  <Divider style={{ margin: 5 }} />
                                  <span>
                                    Em cada linha que este campo estiver
                                    desmarcado, será identificado como se o item
                                    não fosse verificado.
                                  </span>
                                </>
                              }
                            >
                              <FaQuestionCircle />
                            </Tooltip>
                          </Group>
                        </Group>
                      </Group>
                    </Space>
                  ),
                  width: 220,
                  render: (_, row, index: number) => (
                    <>
                      <Form.Item
                        noStyle
                        name={[index, 'response']}
                        getValueFromEvent={(value: string[]) => {
                          const response = value.at(-1);
                          const key = String(row.value);

                          form.setFieldValue(
                            ['items', index, 'description'],
                            row.value,
                          );

                          setSelectedKeys((prev) => {
                            if (!!response) {
                              return prev.includes(key) ? prev : [...prev, key];
                            } else {
                              form.setFieldValue(
                                ['items', index, 'comment'],
                                undefined,
                              );
                              return prev.filter((k) => k !== key);
                            }
                          });

                          return response ? [response] : [];
                        }}
                      >
                        <Checkbox.Group
                          style={{
                            justifyContent: 'space-around',
                            alignItems: 'center',
                            display: 'flex',
                            width: '100%',
                          }}
                        >
                          <Checkbox value="1" />

                          <Divider type="vertical" />

                          <Checkbox value="0" />
                        </Checkbox.Group>
                      </Form.Item>

                      <Form.Item
                        hidden
                        noStyle
                        name={[index, 'description']}
                        initialValue={row.value}
                      />
                    </>
                  ),
                },
                {
                  dataIndex: 'label',
                  title: 'Descrição',
                  ellipsis: true,
                },
                {
                  dataIndex: 'comment',
                  title: 'Comentário',
                  render: (_, row, index) => (
                    <Form.Item name={[index, 'comment']} noStyle>
                      <Input disabled={!selectedKeys.includes(row.value)} />
                    </Form.Item>
                  ),
                },
              ]}
            />
          )}
        </Form.List>
      </Form>

      <WorkorderChecklistFooter />
    </DefaultRouteTabs>
  );
};

export default WorkorderChecklistQuickChecklistPage;
